/**
 * First-touch + last-touch UTM / referrer attribution → cart attributes.
 *
 * Runs only with OneTrust Analytics consent (C0002).
 * Clears stored + cart attribution when analytics consent is withdrawn.
 *
 * First-touch cart attributes (set once):
 *   _utm_source, _utm_medium, _utm_campaign, _utm_content, _utm_term, _first_referrer
 *
 * Last-touch cart attributes (overwrite when new UTMs / external referrer appear):
 *   _last_utm_source, _last_utm_medium, _last_utm_campaign, _last_utm_content,
 *   _last_utm_term, _last_referrer
 */
(function () {
  var STORAGE_PREFIX = 'bb_attr_';
  var CATEGORY_ANALYTICS = 'C0002';
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var CART_ATTR_KEYS = []
    .concat(
      UTM_KEYS.map(function (key) {
        return '_' + key;
      })
    )
    .concat(['_first_referrer'])
    .concat(
      UTM_KEYS.map(function (key) {
        return '_last_' + key;
      })
    )
    .concat(['_last_referrer']);
  var MAX_VALUE_LENGTH = 255;
  var clearedLastKeys = {};

  function hasAnalyticsConsent() {
    var groups = window.OnetrustActiveGroups || window.OptanonActiveGroups || '';
    return groups.indexOf(CATEGORY_ANALYTICS) !== -1;
  }

  function truncate(value) {
    if (!value) return '';
    return String(value).slice(0, MAX_VALUE_LENGTH);
  }

  function storageGet(key) {
    try {
      return localStorage.getItem(STORAGE_PREFIX + key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, value);
    } catch (e) {
      // Private mode / blocked storage — cart write may still work for this pageview.
    }
  }

  function storageRemove(key) {
    try {
      localStorage.removeItem(STORAGE_PREFIX + key);
    } catch (e) {
      // ignore
    }
  }

  function clearStoredAttribution() {
    try {
      Object.keys(localStorage).forEach(function (key) {
        if (key.indexOf(STORAGE_PREFIX) === 0) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      // ignore
    }
  }

  function urlHasUtm(params) {
    return UTM_KEYS.some(function (key) {
      return Boolean(params.get(key));
    });
  }

  function isExternalReferrer() {
    return Boolean(
      document.referrer && document.referrer.indexOf(window.location.hostname) === -1
    );
  }

  function captureFirstTouch(params) {
    // First-touch UTMs: capture once from the first landing that has any UTM.
    if (!storageGet('utm_captured') && urlHasUtm(params)) {
      UTM_KEYS.forEach(function (key) {
        var fromUrl = params.get(key);
        if (fromUrl) storageSet(key, truncate(fromUrl));
      });
      storageSet('utm_captured', '1');
    }

    if (!storageGet('first_referrer') && isExternalReferrer()) {
      storageSet('first_referrer', truncate(document.referrer));
    }
  }

  function captureLastTouch(params) {
    // Replace the whole last-touch UTM set on each new UTM landing so prior
    // campaign keys do not bleed into the latest one.
    if (urlHasUtm(params)) {
      UTM_KEYS.forEach(function (key) {
        var fromUrl = params.get(key);
        if (fromUrl) {
          storageSet('last_' + key, truncate(fromUrl));
          delete clearedLastKeys[key];
        } else {
          storageRemove('last_' + key);
          clearedLastKeys[key] = true;
        }
      });
    }

    if (isExternalReferrer()) {
      storageSet('last_referrer', truncate(document.referrer));
    }
  }

  function buildAttributes() {
    var attributes = {};

    UTM_KEYS.forEach(function (key) {
      var firstVal = storageGet(key);
      if (firstVal) attributes['_' + key] = firstVal;

      var lastVal = storageGet('last_' + key);
      if (lastVal) {
        attributes['_last_' + key] = lastVal;
      } else if (clearedLastKeys[key]) {
        attributes['_last_' + key] = '';
      }
    });

    var firstRef = storageGet('first_referrer');
    if (firstRef) attributes['_first_referrer'] = firstRef;

    var lastRef = storageGet('last_referrer');
    if (lastRef) attributes['_last_referrer'] = lastRef;

    return attributes;
  }

  function emptyCartAttributes() {
    var attributes = {};
    CART_ATTR_KEYS.forEach(function (key) {
      attributes[key] = '';
    });
    return attributes;
  }

  function needsCartUpdate(cartAttributes, nextAttributes) {
    var keys = Object.keys(nextAttributes);
    if (!keys.length) return false;

    var current = cartAttributes || {};
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if ((current[key] || '') !== (nextAttributes[key] || '')) return true;
    }
    return false;
  }

  function updateCartAttributes(attributes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ attributes: attributes })
    });
  }

  function syncCartAttributes(attributes) {
    if (!Object.keys(attributes).length) return;

    fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart.js failed');
        return response.json();
      })
      .then(function (cart) {
        if (!needsCartUpdate(cart.attributes, attributes)) return null;
        return updateCartAttributes(attributes);
      })
      .catch(function () {
        // Non-blocking: attribution should never break browsing/checkout.
      });
  }

  function clearCartAttribution() {
    updateCartAttributes(emptyCartAttributes()).catch(function () {
      // ignore
    });
  }

  function run() {
    if (!hasAnalyticsConsent()) return;

    var params = new URLSearchParams(window.location.search);
    clearedLastKeys = {};
    captureFirstTouch(params);
    captureLastTouch(params);
    syncCartAttributes(buildAttributes());
  }

  function onConsentChange() {
    if (hasAnalyticsConsent()) {
      run();
      return;
    }

    clearStoredAttribution();
    clearCartAttribution();
  }

  run();

  window.addEventListener('OneTrustGroupsUpdated', onConsentChange);
  window.addEventListener('OTConsentApplied', onConsentChange);
})();
